import { create } from 'zustand';

import {
  setLocale as setI18nLocale,
  getLocale,
  isUILocaleSupported,
  SUPPORTED_UI_LOCALES,
  type SupportedUILocale,
} from '../lib/i18n';

type LocaleState = {
  /** Active UI locale code (e.g. 'en', 'fi', 'sv'). */
  locale: SupportedUILocale;

  /** Locale codes available for the current session that also have a UI translation bundle. */
  availableLocales: SupportedUILocale[];

  /**
   * Bumped whenever a translation bundle changes after initial load (e.g. the
   * runtime site-copy overlay, #662). Consumers depend on it so `t()` results
   * refresh without a locale switch.
   */
  bundlesVersion: number;

  /** Signal that translation bundles changed and `t()` consumers must re-render. */
  bumpBundlesVersion: () => void;

  /** Change the active UI locale. */
  setLocale: (locale: string) => void;

  /** Set the list of available UI locales (intersection of session languages and UI bundles). */
  setAvailableLocales: (locales: string[]) => void;

  /**
   * Initialise locale from session language settings.
   *
   * Priority:
   * 1. Browser language if it is among the session's accepted languages AND has a UI bundle.
   * 2. Session primary language if it has a UI bundle.
   * 3. 'en' (always available).
   */
  initFromSession: (primaryLanguage: string, acceptedLanguages: string[]) => void;
};

/** Detect the user's preferred browser language (first 2-char code). */
const getBrowserLocale = (): string => {
  const lang = navigator.language ?? (navigator as any).userLanguage ?? 'en';
  return lang.slice(0, 2).toLowerCase();
};

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: (getLocale() as SupportedUILocale) || 'en',
  availableLocales: ['en', 'fi', 'sv'],
  bundlesVersion: 0,

  bumpBundlesVersion: () => {
    set((state) => ({ bundlesVersion: state.bundlesVersion + 1 }));
  },

  setLocale: (locale: string) => {
    const safe: SupportedUILocale = isUILocaleSupported(locale) ? locale : 'en';
    setI18nLocale(safe);
    set({ locale: safe });
  },

  setAvailableLocales: (locales: string[]) => {
    const filtered = locales.filter(isUILocaleSupported) as SupportedUILocale[];
    // Ensure 'en' is always present
    if (!filtered.includes('en')) {
      filtered.unshift('en');
    }
    set({ availableLocales: filtered });
  },

  initFromSession: (primaryLanguage: string, acceptedLanguages: string[]) => {
    // All supported UI locales are always available — the session's language
    // settings only influence which locale is chosen as the default.
    const available: SupportedUILocale[] = [...SUPPORTED_UI_LOCALES];

    // Pick the best default locale
    const browserLang = getBrowserLocale();
    let chosen: SupportedUILocale = 'en';

    // Session-accepted languages that have a UI bundle
    const sessionLocales = acceptedLanguages.filter(isUILocaleSupported) as SupportedUILocale[];

    if (isUILocaleSupported(browserLang) && sessionLocales.includes(browserLang)) {
      // Browser language matches a session-accepted locale — best match
      chosen = browserLang;
    } else if (sessionLocales.length > 0) {
      // Pick from session-accepted languages: prefer primaryLanguage if it's accepted, else first accepted
      chosen = sessionLocales.includes(primaryLanguage as SupportedUILocale)
        ? (primaryLanguage as SupportedUILocale)
        : sessionLocales[0];
    } else if (isUILocaleSupported(primaryLanguage)) {
      // No session languages with UI bundle — fall back to the host's primary language
      chosen = primaryLanguage as SupportedUILocale;
    }

    setI18nLocale(chosen);
    set({ locale: chosen, availableLocales: available });
  },
}));
